import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getCvTrace } from '../lib/cortivoxApi';
import {
  buildSessionModel,
  buildComparison,
  buildTrend,
  defaultGroup,
  MIN_FRAMES_FOR_SYM,
} from './sessionMetrics';

// >>> CVX COLORS AUTO-BLOCK (не редактировать вручную) >>>
// String.raw — ОБЯЗАТЕЛЬНО: иначе JS в template literal съедает обратные слэши
// в селекторах (hover\:, \/40) и правила вариантов молча умирают.
// ЕДИНЫЙ блок для ВСЕХ страниц (кабинет врача, кабинет пациента, каталог,
// отчёт): какой бы файл ни загрузился первым — набор цветов одинаковый.
const CVX_COLORS_CSS = String.raw`
/* ==========================================================================
   cortivox-cvx.css — цвета кабинетов Cortivox (пациент и врач).
   Готово по демо UX «финал 25.10». НЕ трогает лендинг: это отдельные
   классы bg-cvx-*/text-cvx-*/..., которые нигде больше в проекте не встречаются.
   Подключение: в src/main.jsx после import "./index.css" добавить
     import "./cortivox-cvx.css";
   ========================================================================== */

.bg-cvx-accent{ background-color: #CFEFE0; }
.bg-cvx-accent\/10{ background-color: #CFEFE01A; }
.bg-cvx-background{ background-color: #F5FBF8; }
.bg-cvx-background\/95{ background-color: #F5FBF8F2; }
.bg-cvx-border{ background-color: #D8E2DE; }
.bg-cvx-card{ background-color: #FFFFFF; }
.bg-cvx-card\/60{ background-color: #FFFFFF99; }
.bg-cvx-muted{ background-color: #EDF4F1; }
.bg-cvx-muted-foreground\/50{ background-color: #576C6C80; }
.bg-cvx-popover{ background-color: #FFFFFF; }
.bg-cvx-primary{ background-color: #216858; }
.bg-cvx-primary-foreground\/20{ background-color: #F7FBF933; }
.bg-cvx-primary\/10{ background-color: #2168581A; }
.bg-cvx-primary\/25{ background-color: #21685840; }
.bg-cvx-primary\/40{ background-color: #21685866; }
.bg-cvx-primary\/80{ background-color: #216858CC; }
.bg-cvx-secondary{ background-color: #E8F2EE; }
.bg-cvx-secondary\/20{ background-color: #E8F2EE33; }
.bg-cvx-secondary\/30{ background-color: #E8F2EE4D; }
.bg-cvx-secondary\/40{ background-color: #E8F2EE66; }
.bg-cvx-secondary\/50{ background-color: #E8F2EE80; }
.bg-cvx-secondary\/60{ background-color: #E8F2EE99; }
.bg-cvx-sidebar{ background-color: #F0F9F6; }
.border-cvx-border{ border-color: #D8E2DE; }
.border-cvx-input{ border-color: #D8E2DE; }
.border-cvx-primary{ border-color: #216858; }
.divide-cvx-border{ border-color: #D8E2DE; }
.focus\:border-cvx-primary\/50:focus{ border-color: #21685880; }
.focus\:ring-cvx-ring:focus{ --tw-ring-color: #508E7E; }
.focus\:ring-cvx-ring\/20:focus{ --tw-ring-color: #508E7E33; }
.hover\:bg-cvx-accent:hover{ background-color: #CFEFE0; }
.hover\:bg-cvx-primary\/90:hover{ background-color: #216858E6; }
.hover\:bg-cvx-secondary:hover{ background-color: #E8F2EE; }
.hover\:bg-cvx-secondary\/50:hover{ background-color: #E8F2EE80; }
.hover\:bg-cvx-sidebar-accent:hover{ background-color: #CFEFE0; }
.hover\:border-cvx-destructive\/40:hover{ border-color: #DC262666; }
.hover\:border-cvx-primary\/40:hover{ border-color: #21685866; }
.hover\:border-cvx-primary\/50:hover{ border-color: #21685880; }
.hover\:text-cvx-destructive:hover{ color: #DC2626; }
.hover\:text-cvx-primary:hover{ color: #216858; }
.placeholder\:text-cvx-muted-foreground::placeholder{ color: #576C6C; }
.placeholder\:text-cvx-muted-foreground\/50::placeholder{ color: #576C6C80; }
.placeholder\:text-cvx-muted-foreground\/70::placeholder{ color: #576C6CB3; }
.ring-cvx-primary\/30{ --tw-ring-color: #2168584D; }
.stroke-cvx-primary{ stroke: #216858; }
.stroke-cvx-secondary{ stroke: #E8F2EE; }
.text-cvx-accent-foreground{ color: #094E3E; }
.text-cvx-destructive{ color: #DC2626; }
.text-cvx-foreground{ color: #182729; }
.text-cvx-muted-foreground{ color: #576C6C; }
.text-cvx-muted-foreground\/40{ color: #576C6C66; }
.text-cvx-muted-foreground\/60{ color: #576C6C99; }
.text-cvx-muted-foreground\/80{ color: #576C6CCC; }
.text-cvx-primary{ color: #216858; }
.text-cvx-primary-foreground{ color: #F7FBF9; }
.text-cvx-primary-foreground\/70{ color: #F7FBF9B3; }
.text-cvx-primary\/60{ color: #21685899; }
.text-cvx-secondary-foreground{ color: #18433A; }

/* Правки 26.10 — унификация с лендингом: чёрные заголовки, янтарное
   «Требуют внимания», логотип Cortivox.ai (как на лендинге) */
.bg-cvx-warn{ background-color: #FEF6E3; }
.bg-cvx-warn-card{ background-color: #FFFCF2; }
.border-cvx-warn{ border-color: #EFDFAE; }
.text-cvx-warn{ color: #B45309; }
.text-cvx-logo-ai{ color: #508E7E; }

/* Правки 26.10 v2 — как на лендинге: чёрный — для действий и активных
   состояний (кнопки, выбранные вкладки, логотип), зелёный — только акценты.
   ink = основной чёрный лендинга (shadcn neutral --primary, oklch 0.145 0 0) */
.bg-cvx-ink{ background-color: #0A0A0A; }
.bg-cvx-ink\/40{ background-color: #0A0A0A66; }
.hover\:bg-cvx-ink\/90:hover{ background-color: #0A0A0AE6; }
.text-cvx-ink{ color: #0A0A0A; }
.border-cvx-ink{ border-color: #0A0A0A; }
.hover\:text-cvx-ink:hover{ color: #0A0A0A; }

/* Единая кнопка «Назад» (везде одинаковая: отчёты, карточка пациента, чат) */
.cvx-back{ display:inline-flex; align-items:center; gap:.5rem; height:2.5rem; padding:0 1rem; border-radius:9999px; border:1px solid #D8E2DE; background:#FFFFFF; color:#0A0A0A; font-size:.875rem; font-weight:600; line-height:1; white-space:nowrap; flex:none; cursor:pointer; transition:background-color .15s ease, border-color .15s ease; }
.cvx-back:hover{ background:#E8F2EE; border-color:#C9DAD3; }
.cvx-back:disabled{ opacity:.5; }
.divide-cvx-border > :not([hidden]) ~ :not([hidden]) { border-color: #D8E2DE; }

/* Кастомные классы демо (не Tailwind) */
.cv-shadow { box-shadow: 0 1px 2px #10362f0a, 0 4px 16px #10362f0d; }
.cv-large { font-size: 1.125rem; }
.cv-large .cv-big-btn { border-radius: 1.25rem; height: 4rem; font-size: 1.1875rem; }
.cv-scroll::-webkit-scrollbar { width: 8px; }
.cv-scroll::-webkit-scrollbar-thumb { background: #d7e8e1; border-radius: 8px; }
`;
if (typeof document !== 'undefined') {
  // Версионная защита (26.10, v4): старый блок #cvx-colors (без версии = v0,
  // например из старого файла каталога) ЗАМЕНЯЕТСЯ этим блоком (v5). Раньше guard
  // «уже есть — пропустить» оставлял старые цвета навсегда, и чёрные кнопки/
  // логотип становились прозрачными, а белый текст — невидимым.
  const __cvxPrev = document.getElementById('cvx-colors');
  const __cvxPrevVer = __cvxPrev ? (Number(__cvxPrev.getAttribute('data-cvx-version') || '0') || 0) : 0;
  if (!__cvxPrev || __cvxPrevVer < 5) {
    if (__cvxPrev) __cvxPrev.parentNode.removeChild(__cvxPrev);
    const __cvxStyle = document.createElement('style');
    __cvxStyle.id = 'cvx-colors';
    __cvxStyle.setAttribute('data-cvx-version', '5');
    __cvxStyle.textContent = CVX_COLORS_CSS;
    document.head.appendChild(__cvxStyle);
  }
}
// <<< CVX COLORS AUTO-BLOCK <<<


// Экран отчёта CV-упражнения (секция «Отчеты» кабинета) — по макету v3 и
// сводной спецификации (редакция 2), раздел 6. Пять секций:
//   1. Заключение о записи (+ технические детали по клику)
//   2. Прогресс — «было → стало» и динамика по занятиям
//   3. Разбор сессии — покадровая кривая с порогом и подсказками
//   4. Таблица динамики
//   5. Детализация — замечания по типам
// Пропсы:
//   session       — данные GET /api/cv-reports/{id}/trace ({report, trace, ...})
//   sessions      — список сессий пациента (listCvTraces) для «Прогресса»
//   exerciseTitle — заголовок упражнения из Каталога
//
// Прошлые сессии для сравнения и тренда догружаются здесь же (getCvTrace):
// пока бэкенд не умеет отдавать сводку без trace, берём ближайшие
// HISTORY_MAX сессий того же упражнения.
//
// Обновлено 25.10: семантика category_name подтверждена врачом ("5" — чисто,
// "2" — с замечаниями); группы метрик и подписи сверены с демо-страницей
// /demo/full-metrics; добавлены дорожки «Удержание» и «Пороги персонализации».
// Обновлено 29.10: правило «мало кадров» — симметрия не считается по повторам
// с менее чем 10 кадрами в trace (фантомные повторы в паузе между движениями
// давали выбросы 549%/757% — разобрано с врачом на записи заборчика 24.10);
// на главном графике — полосы повторов (красная — мало кадров); примечания —
// расшифровка чтения симметрии (100% — симметрично, выше/ниже — кто отстаёт).

const HISTORY_MAX = 6;

const C = {
  ink: '#1e293b',
  muted: '#64748b',
  soft: '#94a3b8',
  line: '#e2e8f0',
  accent: '#0f9d8a',
  blue: '#2563eb',
  red: '#dc2626',
  amber: '#d97706',
  grid: '#eef2f7',
};
const STATUS_COLOR = { better: C.accent, worse: C.red, none: C.soft };

const ru = (v) => String(v).replace('.', ',');
const pctStr = (v) => (v === null || v === undefined ? '—' : `${Math.round(v)}%`);
const fmtDateShort = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '')
    : '—';
};
const fmtDateTime = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : '—';
};

function Note({ children }) {
  return <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">{children}</p>;
}

function Kpi({ label, value, tone = '', note }) {
  return (
    <div className="rounded-xl border bg-cvx-border bg-cvx-secondary/40 p-3">
      <div className="text-[11px] text-cvx-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold ${tone === 'red' ? 'text-red-600' : tone === 'ok' ? 'text-emerald-600' : 'text-cvx-ink'}`}>{value}</div>
      {note && <div className="text-[11px] text-cvx-muted-foreground/80">{note}</div>}
    </div>
  );
}

// ---------- Главный график: покадровая кривая ----------
function LineChart({ model, group }) {
  const [hidden, setHidden] = useState(() => new Set());
  const [tip, setTip] = useState(null);
  const boxRef = useRef(null);

  const W = 960;
  const H = 300;
  const M = { l: 48, r: 18, t: 22, b: 32 };
  const pw = W - M.l - M.r;
  const ph = H - M.t - M.b;

  const rowsT = model.series?.t || [];
  const xMax = Math.max(rowsT[rowsT.length - 1] || 1, 1);
  const unitSuffix = group.units === 'deg' ? '°' : group.units === 'ms' ? ' мс' : '%';

  const series = group.lines.map((ln) => ({
    ...ln,
    color: ln.key.endsWith('_left') || group.lines.length === 1 ? C.accent : C.blue,
    values: model.series?.features?.[ln.key] || [],
  }));

  const thresholds = (group.thresholds || [])
    .map((th) => {
      const arr = model.series?.thresholds?.[th.key] || [];
      const v = arr.find((x) => x !== null && x !== undefined) ?? th.value ?? null;
      if (v === null || v === undefined) return null;
      const isThresh = /_(thresh|threshold)$/.test(th.key);
      const label = th.key.endsWith('_max') ? `не выше ${ru(String(Math.round(v)))}${unitSuffix}`
        : th.key.endsWith('_min') ? `не ниже ${ru(String(Math.round(v)))}${unitSuffix}`
          : isThresh ? `порог ${ru(String(Math.round(v)))}${unitSuffix}`
            : `нужно ${ru(String(Math.round(v)))}${unitSuffix}`;
      return { ...th, v, label };
    })
    .filter(Boolean);

  // Диапазон Y: верх — максимум данных с запасом, низ — минимум данных, но не выше 0
  // (уровень покоя всегда в кадре). Раньше ось была жёстко [0…макс] и отрицательные
  // значения «Высоты уголков губ» (уголок ниже покоя) рисовались под осью и обрезались
  // краем SVG — на вкладку это никто не смотрел, пока она не стала графиком симметрии
  // по умолчанию (29.10, заметила врач).
  const vals = [...series.flatMap((s) => s.values), ...thresholds.map((t) => t.v)]
    .filter((v) => v !== null && v !== undefined);
  const yTopRaw = Math.max(1, ...vals) * 1.15;
  const yBotRaw = Math.min(0, ...vals) * 1.15;
  const yStep = group.units === 'ms'
    ? (yTopRaw > 1500 ? 500 : yTopRaw > 400 ? 200 : 100)
    : yTopRaw - yBotRaw > 25 ? 10 : yTopRaw - yBotRaw > 10 ? 5 : 2;
  const yMax = Math.max(yStep, Math.ceil(yTopRaw / yStep) * yStep);
  const yMin = Math.floor(yBotRaw / yStep) * yStep;

  const X = (t) => M.l + (t / xMax) * pw;
  const Y = (v) => M.t + ph - ((v - yMin) / (yMax - yMin)) * ph;
  const xTick = xMax > 90 ? 30 : xMax > 40 ? 15 : xMax > 12 ? 5 : xMax > 5 ? 2 : 1;

  const visible = (name) => !hidden.has(name);

  const path = (values) => {
    let d = '';
    let pen = false;
    for (let i = 0; i < rowsT.length; i += 1) {
      const v = values[i];
      if (v === null || v === undefined) { pen = false; continue; }
      d += `${pen ? 'L' : 'M'}${X(rowsT[i]).toFixed(1)} ${Y(v).toFixed(1)} `;
      pen = true;
    }
    return d;
  };

  const onMove = (e) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || !rowsT.length) return;
    const xSvg = ((e.clientX - rect.left) / rect.width) * W;
    const t = Math.max(0, Math.min(xMax, ((xSvg - M.l) / pw) * xMax));
    const n = rowsT.length;
    const i = Math.max(0, Math.min(n - 1, Math.round((t / xMax) * (n - 1))));
    const parts = [`${ru(rowsT[i].toFixed(2))} с`];
    series.forEach((s) => {
      if (!visible(s.key)) return;
      const v = s.values[i];
      const fmt = (x) => (group.units === 'ms' ? ru(String(Math.round(x))) : ru(Number(x).toFixed(1)));
      parts.push(`${s.label ? `${s.label}: ` : ''}${v === null || v === undefined ? '—' : `${fmt(v)}${unitSuffix}`}`);
    });
    model.flags.filter((f) => Math.abs(f.t - rowsT[i]) < 0.45).forEach((f) => parts.push(f.text));
    const rep = model.repBounds.find((b) => rowsT[i] >= b.t0 && rowsT[i] <= b.t1);
    if (rep) {
      parts.push(`повтор #${rep.id}${rep.known ? '' : ' (оборван)'}`);
      if (rep.frames < MIN_FRAMES_FOR_SYM) parts.push(`мало кадров в записи (${rep.frames}) — симметрия не считается`);
    }
    setTip({ left: Math.min(e.clientX - rect.left + 14, rect.width - 210), top: Math.max(0, e.clientY - rect.top - 12), parts });
  };

  return (
    <div className="relative" ref={boxRef} onMouseMove={onMove} onMouseLeave={() => setTip(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label="Покадровая кривая сессии">
        {/* Полосы повторов: зелёная подсветка — обычный повтор, красная — в записи
            меньше 10 кадров (симметрия по нему не считается). rep_id=0 — простой
            до первого повтора, не подсвечиваем. */}
        {model.repBounds.filter((b) => b.id > 0).map((b) => (
          <rect
            key={`rep-band-${b.id}`}
            x={X(b.t0)}
            y={M.t}
            width={Math.max(2, X(b.t1) - X(b.t0))}
            height={ph}
            fill={b.frames < MIN_FRAMES_FOR_SYM ? C.red : C.accent}
            opacity={b.frames < MIN_FRAMES_FOR_SYM ? 0.14 : 0.07}
          />
        ))}
        {model.gaps.map((g, i) => (
          <rect key={i} x={X(g.t0)} y={M.t} width={Math.max(2, X(g.t1) - X(g.t0))} height={ph} fill={C.line} opacity={0.75} />
        ))}
        {Array.from({ length: Math.round((yMax - yMin) / yStep) + 1 }, (_, k) => yMin + k * yStep).map((yv) => (
          <g key={yv}>
            {/* 0 — уровень покоя; при отрицательной части графика выделяем его */}
            <line x1={M.l} x2={M.l + pw} y1={Y(yv)} y2={Y(yv)} stroke={yv === 0 && yMin < 0 ? '#94a3b8' : C.grid} />
            <text x={M.l - 6} y={Y(yv) + 4} textAnchor="end" fontSize={10} fill={C.soft}>{yv}{unitSuffix}</text>
          </g>
        ))}
        {Array.from({ length: Math.floor(xMax / xTick) + 1 }, (_, k) => k * xTick).map((tv) => (
          <g key={tv}>
            <line x1={X(tv)} x2={X(tv)} y1={M.t + ph} y2={M.t + ph + 4} stroke="#cbd5e1" />
            <text x={X(tv)} y={M.t + ph + 15} textAnchor="middle" fontSize={10} fill={C.soft}>{tv}</text>
          </g>
        ))}
        <text x={M.l + pw / 2} y={H - 2} textAnchor="middle" fontSize={10} fill={C.soft}>секунды от начала упражнения</text>
        {thresholds.map((th) => (
          <g key={th.key}>
            <line x1={M.l} x2={M.l + pw} y1={Y(th.v)} y2={Y(th.v)} stroke={C.red} strokeDasharray="6 3" strokeWidth={1.3} />
            <text x={M.l + pw - 4} y={Y(th.v) - 4} textAnchor="end" fontSize={10} fill={C.red}>{th.label}</text>
          </g>
        ))}
        {model.repBounds.map((b) => (
          <g key={b.id}>
            <line x1={X(b.t0)} x2={X(b.t0)} y1={M.t} y2={M.t + ph} stroke="#cbd5e1" />
            <text x={X(b.t0) + 4} y={M.t + 12} fontSize={10} fill={C.muted}>{`#${b.id}`}</text>
          </g>
        ))}
        {series.filter((s) => visible(s.key)).map((s) => (
          <path key={s.key} d={path(s.values)} fill="none" stroke={s.color} strokeWidth={1.8} strokeLinejoin="round" />
        ))}
        {model.flags.map((f, i) => (
          <g key={i}>
            <line x1={X(f.t)} x2={X(f.t)} y1={M.t + 15} y2={M.t + 25} stroke={C.amber} strokeWidth={1.2} strokeDasharray="2 2" />
            {model.flags.length <= 6 && (
              <text x={X(f.t) + 3} y={M.t + 24} fontSize={9} fill={C.amber} fontWeight={600}>{f.text.slice(0, 22)}</text>
            )}
          </g>
        ))}
      </svg>
      {tip && (
        <div
          className="pointer-events-none absolute z-10 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-2 text-[11px] leading-4 text-white shadow-lg"
          style={{ left: tip.left, top: tip.top }}
        >
          {tip.parts.map((p, i) => <div key={i}>{p}</div>)}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {series.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setHidden((h) => {
              const next = new Set(h);
              if (next.has(s.key)) next.delete(s.key); else next.add(s.key);
              return next;
            })}
            className={`inline-flex items-center gap-1.5 ${hidden.has(s.key) ? 'opacity-35' : ''}`}
          >
            <span className="inline-block h-1 w-3.5 rounded" style={{ background: s.color }} />
            {s.label || group.label}
          </button>
        ))}
        {thresholds.map((th) => (
          <span key={th.key} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-3.5 rounded" style={{ background: C.red }} />
            {th.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------- Прогресс: «было → стало» ----------
function CompareChart({ rows, prevLabel, curLabel }) {
  const W = 960;
  const rowH = 40;
  const mL = 215;
  const mR = 200;
  const mT = 34;
  const h = mT + rows.length * rowH + 22;
  const pw = W - mL - mR;
  const r1 = mL + pw * 0.24;
  const r2 = mL + pw * 0.62;
  return (
    <svg viewBox={`0 0 ${W} ${h}`} className="block w-full" role="img" aria-label="Сравнение с прошлой сессией">
      <text x={r1} y={16} textAnchor="middle" fontSize={11} fill={C.muted}>{`было · ${prevLabel}`}</text>
      <text x={r2} y={16} textAnchor="middle" fontSize={11} fill={C.muted}>{`стало · ${curLabel}`}</text>
      <text x={mL + pw + 10} y={16} fontSize={11} fill={C.muted}>изменение</text>
      {rows.map((r, i) => {
        const y = mT + i * rowH + rowH / 2;
        const c = STATUS_COLOR[r.status] || C.soft;
        const dim = r.status === 'none';
        return (
          <g key={r.name}>
            <text x={mL - 10} y={y + 4} textAnchor="end" fontSize={12} fill="#334155">{r.name}</text>
            <line x1={r1} x2={r1} y1={y - 14} y2={y + 14} stroke={C.line} strokeWidth={2} />
            <line x1={r2} x2={r2} y1={y - 14} y2={y + 14} stroke={C.line} strokeWidth={2} />
            <line x1={r1 + 7} x2={r2 - 7} y1={y} y2={y} stroke={c} strokeWidth={2.5} opacity={dim ? 0.4 : 1} />
            <circle cx={r1} cy={y} r={5} fill={C.soft} />
            <circle cx={r2} cy={y} r={5.5} fill={c} opacity={dim ? 0.55 : 1} />
            <text x={r1 - 9} y={y + 4} textAnchor="end" fontSize={11.5} fill="#475569">{r.was}</text>
            <text x={r2 + 10} y={y + 4} fontSize={11.5} fontWeight={600} fill={dim ? '#475569' : c}>{r.now}</text>
            <text x={mL + pw + 10} y={y + 4} fontSize={11.5} fill={dim ? '#b45309' : c}>{r.delta}</text>
          </g>
        );
      })}
      <text x={mL} y={h - 4} fontSize={10} fill={C.soft}>
        зелёная точка — стало лучше · красная — хуже · серая строка — сравнение некорректно (обрыв сессии)
      </text>
    </svg>
  );
}

// ---------- Прогресс: динамика по занятиям ----------
function TrendChart({ points }) {
  const W = 960;
  const H = 230;
  const M = { l: 46, r: 18, t: 22, b: 32 };
  const pw = W - M.l - M.r;
  const ph = H - M.t - M.b;
  const yMax = Math.max(1, ...points.map((p) => p.total));
  const X = (i) => M.l + (i + 0.5) * (pw / points.length);
  const Y = (v) => M.t + ph - (v / yMax) * ph;
  const changedIdx = points.findIndex((p) => p.thresholdsChanged);
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label="Динамика правильных повторов">
      {Array.from({ length: yMax + 1 }, (_, k) => k).map((yv) => (
        <g key={yv}>
          <line x1={M.l} x2={M.l + pw} y1={Y(yv)} y2={Y(yv)} stroke={C.grid} />
          <text x={M.l - 6} y={Y(yv) + 4} textAnchor="end" fontSize={10} fill={C.soft}>{yv}</text>
        </g>
      ))}
      {changedIdx >= 0 && (
        <g>
          <line x1={X(changedIdx)} x2={X(changedIdx)} y1={M.t} y2={M.t + ph} stroke={C.amber} strokeDasharray="4 3" />
          <text x={X(changedIdx) + 5} y={M.t + 12} fontSize={10} fill={C.amber}>сменились пороги — цель выросла</text>
        </g>
      )}
      <path d={points.map((p, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(p.correct).toFixed(1)}`).join(' ')} fill="none" stroke={C.accent} strokeWidth={2} />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={X(i)} cy={Y(p.correct)} r={p.isLast ? 5 : 3.5} fill={p.isLast && p.aborted ? C.red : C.accent} />
          <text x={X(i)} y={M.t + ph + 15} textAnchor="middle" fontSize={10} fill={C.soft}>{p.label}</text>
          {(!p.isLast || !p.aborted) && (
            <text x={X(i)} y={Y(p.correct) - 9} textAnchor="middle" fontSize={10} fill="#475569">{`${p.correct} из ${p.total}`}</text>
          )}
        </g>
      ))}
      {last?.aborted && (
        <text x={X(points.length - 1) - 8} y={Y(last.correct) - 10} textAnchor="end" fontSize={10} fill={C.red}>
          {`${last.correct} из ${last.total} — обрыв: ${last.abortText || 'сессия прервана'}`}
        </text>
      )}
      <text x={M.l + pw / 2} y={H - 2} textAnchor="middle" fontSize={10} fill={C.soft}>дата занятия · правильные повторы</text>
    </svg>
  );
}

// ---------- Детализация: замечания по типам ----------
function RemarkBars({ bars }) {
  const max = Math.max(...bars.map((b) => b.n), 1);
  return (
    <div className="flex flex-col gap-1.5">
      {bars.map((b) => (
        <div key={b.code} className="flex items-center gap-2.5 text-[13px]">
          <div className="w-[270px] max-w-[45%] shrink-0 truncate text-slate-700">{b.text}</div>
          <div className="h-[18px] flex-1 overflow-hidden rounded-md bg-slate-100">
            <div className="h-full rounded-md bg-red-600" style={{ width: `${Math.round((b.n / max) * 100)}%` }} />
          </div>
          <div className="w-10 shrink-0 text-right tabular-nums text-slate-600">{b.n}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- Таблица динамики ----------
function DynamicsTable({ model, compare, exerciseTitle }) {
  const idShort = model.calibration?.id ? String(model.calibration.id).slice(0, 8) : null;
  const updCount = Object.keys(model.thresholdsUpdated || {}).length;
  const repDelta = compare?.[0];
  const rows = [
    ['Дата и время', fmtDateTime(model.createdAt)],
    ['Упражнение', exerciseTitle || model.exerciseSlug || '—'],
    ['Калибровка', model.calibration ? `${model.calibration.measured ? 'измерена в этой сессии' : 'сохранённая'}${idShort ? ` · ${idShort}` : ''}` : '—'],
    ['Пороги', updCount ? `персонализированные (${updCount})` : 'по умолчанию'],
    ['Качество записи', model.quality.pct === null ? 'нет записи' : `${model.quality.label} · ${model.quality.pct}% кадров`],
    ['Симметрия движения', model.amplitude?.symMedianPct != null ? `${Math.round(model.amplitude.symMedianPct)}% · слабая — ${model.amplitude.weakerSide}` : '—'],
    ['Амплитуда Л / П', model.amplitude?.leftPct != null ? `${Math.round(model.amplitude.leftPct)}% / ${Math.round(model.amplitude.rightPct)}% (пик за повтор, % от опорного размера лица)` : '—'],
    ['Удержание', model.hold.text || (model.hold.fact ? `фактически до ${ru(String(model.hold.fact.sec))} с${model.hold.fact.targetSec ? ` из ${ru(String(model.hold.fact.targetSec))} с` : ''}` : '—')],
    ['Повторы', `${model.correct.count} из ${model.correct.total}`],
    ['Изменение к прошлой', repDelta ? repDelta.delta : '—'],
    ['Статус сравнения', model.abort ? 'некорректно — обрыв' : repDelta && repDelta.status !== 'none' ? (repDelta.status === 'better' ? 'стало лучше' : 'стало хуже') : '—'],
    ['Дней от события', '—'],
    ['Главное замечание', model.remarksBars[0] ? `${model.remarksBars[0].text} — ${model.remarksBars[0].n} повт.` : '—'],
    ['Комментарий врача', '(поле появится позже)'],
  ];
  return (
    <table className="w-full border-collapse text-xs">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <th className="border bg-cvx-border bg-cvx-secondary/40 px-2 py-1.5 text-left font-semibold text-slate-600">{k}</th>
            <td className="border bg-cvx-border px-2 py-1.5 text-cvx-foreground">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------- Экран целиком ----------
export default function SessionReport({ session, sessions = [], exerciseTitle }) {
  const report = session?.report || {};
  const trace = session?.trace || null;

  const model = useMemo(
    () => buildSessionModel(report, trace, { createdAt: session?.createdAt, exerciseSlug: session?.exerciseSlug }),
    [session],
  );

  // Прошлые сессии того же упражнения — источник сравнения и тренда.
  const historyMeta = useMemo(
    () => sessions.filter((s) => s?.exerciseSlug === session?.exerciseSlug && s?.id && s.id !== session?.id).slice(0, HISTORY_MAX - 1),
    [sessions, session],
  );
  const [history, setHistory] = useState({});
  const inflight = useRef(new Set());
  useEffect(() => {
    let alive = true;
    historyMeta.forEach((meta) => {
      if (inflight.current.has(meta.id)) return;
      inflight.current.add(meta.id);
      getCvTrace(meta.id)
        .then((d) => {
          if (!alive) return;
          setHistory((h) => ({
            ...h,
            [meta.id]: {
              model: buildSessionModel(d?.report || {}, d?.trace || null, { createdAt: d?.createdAt, exerciseSlug: d?.exerciseSlug }),
              createdAt: d?.createdAt || meta.createdAt,
            },
          }));
        })
        .catch(() => {
          if (alive) setHistory((h) => ({ ...h, [meta.id]: { error: true } }));
        })
        .finally(() => inflight.current.delete(meta.id));
    });
    return () => { alive = false; };
  }, [historyMeta]);

  const prev = useMemo(() => {
    const curT = session?.createdAt ? new Date(session.createdAt).getTime() : null;
    const entries = Object.entries(history)
      .map(([, h]) => (h?.model ? h : null))
      .filter((h) => h && !h.error)
      .filter((h) => curT === null || !h.createdAt || new Date(h.createdAt).getTime() < curT);
    if (!entries.length) return null;
    entries.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    return entries[0].model;
  }, [history, session]);

  const trend = useMemo(() => {
    const entries = [{ createdAt: session?.createdAt, model }];
    Object.values(history).forEach((h) => { if (h?.model) entries.push({ createdAt: h.createdAt, model: h.model }); });
    return buildTrend(entries);
  }, [history, model, session]);

  const compare = useMemo(() => (prev ? buildComparison(model, prev) : null), [model, prev]);

  const [groupKey, setGroupKey] = useState(null);
  const group = useMemo(() => {
    const gs = model.groups;
    if (!gs.length) return null;
    return gs.find((g) => g.key === groupKey) || defaultGroup(gs, model.exerciseSlug);
  }, [model.groups, groupKey]);

  const historyLoading = historyMeta.some((m) => !history[m.id]);
  const upd = model.thresholdsUpdated || {};
  const repForTest = report; // «Полные данные отчёта» — тело отчёта без trace

  return (
    <div className="flex flex-col gap-3 text-sm">
      {/* 1. Заключение о записи */}
      <section className="rounded-xl border bg-cvx-border bg-cvx-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-cvx-ink">Заключение о записи</h3>
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-[13px] leading-relaxed">
          <span className="shrink-0 rounded-lg bg-emerald-600 px-2.5 py-0.5 font-semibold text-white">{model.badge}</span>
          <div>
            {model.badgeReason && <span className="font-semibold">{model.badgeReason}</span>}
            {model.abort && <> Сессия оборвана: {model.abort.text}, поэтому «{model.correct.count} из {model.correct.total}» — не показатель регресса.</>}
            {!model.abort && model.quality.pct !== null && model.quality.pct < 95 && <> Кривые читайте с оглядкой на качество записи.</>}
          </div>
        </div>
        <details className="mt-3">
          <summary className="cursor-pointer text-[13px] font-semibold text-teal-700">Технические детали (для пилотов и контрольного тестирования)</summary>
          <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
            <Kpi
              label="Правильные повторы"
              value={`${model.correct.count} из ${model.correct.total}`}
              tone={model.correct.count === 0 ? 'red' : 'ok'}
              note={model.abort ? 'сессия оборвана' : compare ? `${compare[0].delta}` : 'первая сессия — сравнения нет'}
            />
            <Kpi label="С замечаниями" value={`${model.withRemarks.pct}%`} tone={model.withRemarks.pct > 0 ? 'red' : 'ok'} note={`${model.withRemarks.count} повт. из ${model.totalReps}`} />
            <Kpi label="Удержание" value={model.hold.text || (model.hold.fact ? `${ru(String(model.hold.fact.sec))} с` : '—')} note={model.hold.fact ? `факт: до ${ru(String(model.hold.fact.sec))} с${model.hold.fact.targetSec ? ` из ${ru(String(model.hold.fact.targetSec))} с` : ''}` : undefined} />
            <Kpi label="Качество записи" value={model.quality.label || '—'} tone={model.quality.pct === null || model.quality.pct < 80 ? 'red' : 'ok'} note={model.badgeReason} />
            <Kpi label="Статус сессии" value={model.abort ? 'Оборвана' : 'Завершена'} tone={model.abort ? 'red' : 'ok'} note={model.abort ? `причина: ${model.abort.text}` : undefined} />
          </div>
          <div className="mt-2 flex flex-col gap-1 text-xs">
            <p>
              <span className="text-cvx-muted-foreground">Повторов в отчёте: </span>
              {model.totalReps}
            </p>
            <p>
              <span className="text-cvx-muted-foreground">Калибровка: </span>
              {model.calibration ? (model.calibration.measured ? 'измерена в этой сессии' : 'использована сохранённая') : '—'}
            </p>
            {Object.keys(upd).length > 0 && (
              <p>
                <span className="text-cvx-muted-foreground">Подобранные пороги: </span>
                <span className="break-all font-mono">{JSON.stringify(upd)}</span>
              </p>
            )}
            {model.correct.source === 'report' && model.hasTrace && (
              <p className="text-cvx-muted-foreground">Правило зачёта: предварительное (без ответа CV-команды) — по отчёту.</p>
            )}
            {model.truncated && <p className="text-amber-700">Запись укорочена сервером (лимит кадров) — конец сессии не попал в trace.</p>}
          </div>
        </details>
      </section>

      {/* 2. Прогресс */}
      <section className="rounded-xl border bg-cvx-border bg-cvx-card p-4">
        <h3 className="mb-1 text-sm font-semibold text-cvx-ink">Прогресс — с чего врач начинает</h3>
        <Note>Главный вопрос: стало ли лучше, чем в прошлый раз.</Note>
        {historyMeta.length === 0 ? (
          <Note>Это первая сессия упражнения — сравнивать пока не с чем.</Note>
        ) : historyLoading ? (
          <Note>Сравниваю с прошлыми занятиями…</Note>
        ) : compare ? (
          <CompareChart rows={compare} prevLabel={fmtDateShort(prev.createdAt)} curLabel={fmtDateShort(session?.createdAt)} />
        ) : (
          <Note>Прошлые сессии этого упражнения не загрузились — сравнение появится при следующем открытии.</Note>
        )}
        <Note>
          <b>Как читать.</b> Амплитуды — в % от опорного размера лица (эталон из калибровки). Строка «Правильные повторы» серая,
          если сессия оборвана: обрыв делает сравнение некорректным — это не регресс.
        </Note>
        <hr className="my-3 bg-cvx-border" />
        <h4 className="mb-1 text-[13px] font-semibold text-cvx-ink">Динамика по занятиям</h4>
        {trend.length > 1 ? (
          <TrendChart points={trend} />
        ) : (
          <Note>Пока мало занятий: тренд появится после следующих тренировок этого упражнения.</Note>
        )}
        <Note>
          Метрика — <b>правильные повторы</b>: выполнены все три условия (растянул губы, поднял уголки, удержал позу).
          Жёлтая отметка — сменились пороги (цель выросла — это прогресс). Последняя точка красная при обрыве сессии.
        </Note>
      </section>

      {/* 3. Разбор сессии */}
      <section className="rounded-xl border bg-cvx-border bg-cvx-card p-4">
        <h3 className="mb-1 text-sm font-semibold text-cvx-ink">Разбор сессии — главный график</h3>
        {!model.hasTrace || !group ? (
          <Note>Покадровая запись недоступна — разбор по кадрам появится, когда сервер пришлёт trace.</Note>
        ) : (
          <>
            {model.groups.length > 1 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {model.groups.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => setGroupKey(g.key)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${g.key === group.key ? 'border-cvx-ink bg-cvx-ink text-cvx-primary-foreground shadow' : 'bg-cvx-border text-cvx-muted-foreground hover:border-cvx-primary/40'}`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            )}
            <LineChart model={model} group={group} />
            {model.amplitude && model.amplitude.groupKey === group.key && (
              model.amplitude.symPending ? (
                <Note>
                  <b>Симметрия по повторам.</b> Пара для симметрии — «{model.amplitude.groupLabel}» (движение
                  каждого уголка от покоя). Число симметрии пока не показываем: значения пары могут уходить ниже
                  нуля (уголок ниже покоя), и формула для таких величин уточняется у CV-команды. Прежний расчёт
                  по половинам ширины рта показывал положение опорной точки трекинга, а не симметрию, и убран.
                </Note>
              ) : (
                <Note>
                  Симметрия по повторам (слабая — {model.amplitude.weakerSide}), %:{' '}
                  <b>
                    {model.amplitude.perRep
                      .filter((p) => p.known)
                      .map((p) => (p.sym != null ? Math.round(p.sym) : '—'))
                      .join(' · ')}
                  </b>
                  {model.amplitude.symMedianPct != null && <> — медиана {Math.round(model.amplitude.symMedianPct)}%.</>}
                  {' '}Прочерк — амплитуда слишком мала: в записи такого повтора меньше 10 кадров (обычно это пауза между
                  движениями), симметрия по нему не считается.
                </Note>
              )
            )}
            <Note>
              <b>Как читать график.</b> Широкие зелёные полосы — повторы; красная полоса — повтор, в записи которого
              меньше 10 кадров (его нет в списке симметрии выше). Узкие серые полосы — кадры, где лицо не считывалось
              (качество). Жёлтая метка — подсказка, которую пациент услышал в этот момент. Порог — линия «нужно».
            </Note>
            <Note>
              <b>Как читать симметрию.</b> 100% — левый и правый уголки растянулись одинаково, движение симметрично.
              Выше 100% — сильнее левый уголок, отстаёт правая сторона; ниже 100% — сильнее правый, отстаёт левая.
              Чем ближе к 100%, тем симметричнее. Итог берётся как медиана по повторам — один неудачный повтор её не портит.
            </Note>
            <Note>
              По вертикали — {group.units === 'deg' ? 'углы в градусах' : group.units === 'ms' ? 'миллисекунды удержания позы' : '% от опорного размера лица (эталон из калибровки) — 100% это типичный размер лица пациента'}.
            </Note>
          </>
        )}
      </section>

      {/* 4. Таблица динамики */}
      <section className="rounded-xl border bg-cvx-border bg-cvx-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-cvx-ink">Таблица динамики</h3>
        <DynamicsTable model={model} compare={compare} exerciseTitle={exerciseTitle} />
        {model.amplitude?.symMedianPct != null && (
          <Note>
            <b>Симметрия движения</b> — медиана отношения «пик левого уголка / пик правого» по повторам, в %.
            100% — симметрично; выше 100% — сильнее левый, отстаёт правый; ниже — сильнее правый, отстаёт левый.
          </Note>
        )}
      </section>

      {/* 5. Детализация */}
      <section className="rounded-xl border bg-cvx-border bg-cvx-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-cvx-ink">Детализация: замечания по типам</h3>
        {model.remarksBars.length ? (
          <>
            <RemarkBars bars={model.remarksBars} />
            <Note>Сумма столбцов может превышать число повторов с замечаниями: у одного повтора бывает несколько замечаний.</Note>
          </>
        ) : (
          <Note>Замечаний нет — все повторы чистые.</Note>
        )}
      </section>

      <details className="rounded-xl border bg-cvx-border bg-cvx-card p-4">
        <summary className="cursor-pointer text-[13px] text-cvx-muted-foreground hover:text-cvx-ink">Полные данные отчёта</summary>
        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg border bg-cvx-border bg-cvx-secondary/30 p-2 font-mono text-[10px] leading-4 text-cvx-foreground">
          {JSON.stringify(repForTest, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Обёртка для списков отчётов (ЛК пациента и карточка врача):
// свёрнуто — кнопка «Разбор сессии»; развёрнуто — разбор открывается на ВСЮ
// СТРАНИЦУ (полноэкранный слой с крупным шрифтом — врач: «мелкий нечитаем»),
// сверху липкая кнопка «← Назад к списку», Esc тоже закрывает. Полный отчёт
// (report + trace, несколько МБ) докруживается только при открытии —
// список остаётся лёгким.
export function SessionReportExpand({ row, sessions = [], exerciseTitle }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const [error, setError] = useState('');

  const title = exerciseTitle || row?.exerciseSlug || 'Сессия';

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next || full || error || !row?.id) return;
    getCvTrace(row.id)
      .then((d) => setFull(d || {}))
      .catch((e) => setError(
        e?.status === 404
          ? 'Разбор недоступен: сервер ещё не обновлён (нужен новый cv_reports.py и рестарт API)'
          : e?.message || 'Не удалось загрузить разбор сессии',
      ));
  };

  // Полноэкранный слой: Esc закрывает, страница под ним не прокручивается.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="inline-flex w-fit items-center gap-2 rounded-full bg-cvx-ink px-5 py-2.5 text-sm font-semibold text-cvx-primary-foreground transition-colors hover:bg-cvx-ink/90"
      >
        Разбор сессии
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-cvx-background"
          role="dialog"
          aria-modal="true"
          aria-label={`Разбор сессии: ${title}`}
        >
          {/* zoom 1.25 — крупнее шрифт и графики на весь экран (читаемость для врача и пациента) */}
          <div className="mx-auto max-w-5xl px-5 pb-10" style={{ zoom: 1.25 }}>
            <div className="sticky top-0 z-10 -mx-5 mb-4 flex items-center justify-between gap-3 border-b border-cvx-border bg-cvx-background/95 px-5 py-3 backdrop-blur">
              {/* Единая кнопка «Назад» — компактная пилюля, как «Все пациенты» и «Назад» в чате */}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="cvx-back"
              >
                <ArrowLeft className="size-4" /> Назад к списку
              </button>
              <span className="min-w-0 truncate text-base font-bold text-cvx-ink">{title}</span>
            </div>
            {error ? (
              <p className="rounded-2xl border border-cvx-border bg-cvx-card p-5 text-base text-cvx-destructive">{error}</p>
            ) : !full ? (
              <p className="flex items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-card p-5 text-base text-cvx-muted-foreground">
                <Loader2 className="size-5 animate-spin" /> Загружаем запись сессии — это несколько секунд…
              </p>
            ) : (
              <SessionReport session={full} sessions={sessions} exerciseTitle={title} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
